import { z } from 'zod';
import { ASSET_TYPES, SPLITTERS } from './constants';

/**
 * Default prices (PRD ACC-04) in `default_pricing` (key → Rupiah). Used when an asset has no price
 * of its own and "hitung otomatis" is on, and for cables without a price per metre in the cable type.
 */
export interface PriceKey {
  key: string;
  label: string;
  unit: string;
  group: 'Perangkat & titik' | 'Kabel & tiang' | 'Splitter' | 'Jasa';
}

export function unitPriceKey(typeCode: string): string {
  return typeCode === 'POLE' ? 'pole_unit' : `unit_${typeCode}`;
}

export function splitterPriceKey(ratio: string): string {
  return `splitter_${ratio}`;
}

export const PRICE_KEYS: PriceKey[] = [
  { key: 'pole_unit', label: 'Tiang tumpu', unit: 'per tiang', group: 'Kabel & tiang' },
  {
    key: 'cable_per_m',
    label: 'Kabel fiber (bila tipe kabel tanpa harga)',
    unit: 'per meter',
    group: 'Kabel & tiang',
  },
  ...ASSET_TYPES.filter((t) => t.code !== 'POLE').map((t) => ({
    key: unitPriceKey(t.code),
    label: t.name,
    unit: 'per unit',
    group: 'Perangkat & titik' as const,
  })),
  ...[...new Set(SPLITTERS.map((s) => s.ratio))].map((r) => ({
    key: splitterPriceKey(r),
    label: `Splitter ${r}`,
    unit: 'per unit',
    group: 'Splitter' as const,
  })),
  { key: 'labor_pct', label: 'Jasa pemasangan', unit: '% dari material', group: 'Jasa' },
];
const KNOWN_KEYS = new Set(PRICE_KEYS.map((k) => k.key));

export interface PricingDefaults {
  prices: Record<string, number | null>;
  /** Use default prices for assets without their own price (and for new auto poles). */
  autoCalculate: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const pricingUpdateSchema = z.object({
  prices: z
    .record(z.number().min(0).max(100_000_000_000).nullable())
    .refine((p) => Object.keys(p).every((k) => KNOWN_KEYS.has(k)), 'Jenis harga tidak dikenal')
    .refine((p) => p.labor_pct == null || p.labor_pct <= 100, 'Jasa maksimal 100%'),
  autoCalculate: z.boolean(),
});
export type PricingUpdate = z.infer<typeof pricingUpdateSchema>;

// ------------------------------------------------------------------ summary (ACC-01/02)

export interface CostTypeRow {
  typeCode: string;
  count: number;
  /** Assets with their own price. */
  priced: number;
  total: number;
  avg: number | null;
  min: number | null;
  max: number | null;
  pct: number;
}

export interface CostSummary {
  assetCount: number;
  totalValue: number;
  categories: number;
  avgPrice: number | null;
  byType: CostTypeRow[];
  cable: { lengthM: number; value: number; routes: number };
  grandTotal: number;
  autoCalculate: boolean;
}

// ------------------------------------------------------------------ detail (ACC-03)

export const costAssetsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(100).optional(),
  area: z.string().max(40).optional(),
  type: z.string().max(12).optional(),
  status: z.string().max(20).optional(),
  sort: z.enum(['price_desc', 'price_asc', 'code', 'type']).default('code'),
  format: z.enum(['xlsx', 'csv']).optional(),
});
export type CostAssetsQuery = z.infer<typeof costAssetsQuerySchema>;

export interface CostAssetRow {
  id: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
  status: string;
  model: string | null;
  price: number | null;
  priceSource: 'asset' | 'default' | null;
}

// ------------------------------------------------------------------ BOQ / RAB (ACC-07/08)

export const BOQ_SCOPES = ['all', 'planned', 'installed'] as const;
export type BoqScope = (typeof BOQ_SCOPES)[number];
export const BOQ_SCOPE_LABELS: Record<BoqScope, string> = {
  all: 'Semua',
  planned: 'Rencana',
  installed: 'Terpasang',
};

export const boqQuerySchema = z.object({
  area: z.string().max(40).optional(),
  scope: z.enum(BOQ_SCOPES).default('all'),
  format: z.enum(['xlsx']).optional(),
});
export type BoqQuery = z.infer<typeof boqQuerySchema>;

export interface BoqLine {
  group: string;
  item: string;
  unit: string;
  qtyPlanned: number;
  qtyInstalled: number;
  /** Quantity counted in the total for the chosen scope. */
  qty: number;
  unitPrice: number | null;
  total: number;
  /** Some items had no price (neither own nor default). */
  missingPrice: boolean;
}

export interface Boq {
  scope: BoqScope;
  lines: BoqLine[];
  material: number;
  laborPct: number;
  labor: number;
  total: number;
  generatedAt: string;
}
